/**
 * BulkActionBar — 批量操作浮动工具栏
 *
 * 当 Grid 中有行被选中时显示在底部。
 * 支持：批量状态变更 / 批量删除 / 导出选中行
 */

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '@/lib/api';
import { useToast } from '@/lib/toast';
import type { EntityMeta, ActionDef } from '@/types/entity';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import {
  ArrowRightLeft,
  Trash2,
  Download,
  Loader2,
  X,
} from 'lucide-react';

// ─── Types ─────────────────────────────────────────

export interface BulkActionBarProps {
  slug: string;
  meta: EntityMeta;
  selectedIds: string[];
  onClearSelection: () => void;
  onRefresh: () => void;
}

// ─── Component ─────────────────────────────────────

export function BulkActionBar({
  slug,
  meta,
  selectedIds,
  onClearSelection,
  onRefresh,
}: BulkActionBarProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const [loading, setLoading] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showActionPicker, setShowActionPicker] = useState(false);

  const count = selectedIds.length;
  if (count === 0) return null;

  // Available actions for this entity
  const actions: ActionDef[] = meta.actions ?? [];

  // ─── Batch Status Change ──────────────────────────
  const handleBatchAction = useCallback(
    async (actionName: string, actionLabel: string) => {
      setLoading(actionName);
      setShowActionPicker(false);
      try {
        const result = await api.post<{
          success: number;
          failed: number;
          errors: Array<{ id: string; message: string }>;
        }>(`/entities/${slug}/batch-action`, {
          ids: selectedIds,
          action: actionName,
        });
        toast.success(
          t('bulk.actionSuccess', {
            defaultValue: '{{action}}: {{success}} succeeded, {{failed}} failed',
            action: actionLabel,
            success: result.success,
            failed: result.failed,
          }),
        );
        if (result.success > 0) {
          onClearSelection();
          onRefresh();
        }
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : t('bulk.actionFailed', 'Batch action failed'),
        );
      } finally {
        setLoading(null);
      }
    },
    [slug, selectedIds, toast, t, onClearSelection, onRefresh],
  );

  // ─── Batch Delete ─────────────────────────────────
  const handleBatchDelete = useCallback(async () => {
    setLoading('delete');
    try {
      const result = await api.post<{
        deleted: number;
        failed: number;
        errors: Array<{ id: string; message: string }>;
      }>(`/entities/${slug}/batch-delete`, {
        ids: selectedIds,
      });
      toast.success(
        t('bulk.deleteSuccess', {
          defaultValue: 'Deleted {{deleted}}, failed {{failed}}',
          deleted: result.deleted,
          failed: result.failed,
        }),
      );
      if (result.deleted > 0) {
        onClearSelection();
        onRefresh();
      }
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : t('bulk.deleteFailed', 'Batch delete failed'),
      );
    } finally {
      setLoading(null);
      setShowDeleteConfirm(false);
    }
  }, [slug, selectedIds, toast, t, onClearSelection, onRefresh]);

  // ─── Export Selected ──────────────────────────────
  const handleExportSelected = useCallback(() => {
    // Build CSV from selected IDs (simple ID export)
    const header = 'id';
    const csv = [header, ...selectedIds].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${slug}-selected-${count}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(t('bulk.exportSuccess', { defaultValue: 'Exported {{count}} records', count }));
  }, [slug, selectedIds, count, toast, t]);

  return (
    <>
      <ConfirmDialog
        open={showDeleteConfirm}
        title={t('bulk.confirmDeleteTitle', { defaultValue: 'Delete {{count}} records?', count })}
        description={t('bulk.confirmDeleteDesc', {
          defaultValue: 'This will delete {{count}} selected {{label}} records. This action cannot be undone.',
          count,
          label: meta.label,
        })}
        confirmLabel={t('bulk.deleteConfirm', 'Delete All')}
        variant="danger"
        loading={loading === 'delete'}
        onConfirm={handleBatchDelete}
        onCancel={() => setShowDeleteConfirm(false)}
      />

      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 rounded-lg border bg-background/95 backdrop-blur-sm shadow-lg px-4 py-2.5">
        {/* Count */}
        <span className="text-sm font-medium whitespace-nowrap">
          {t('bulk.selected', { defaultValue: '{{count}} selected', count })}
        </span>

        <div className="h-4 w-px bg-border" />

        {/* Batch Status Actions */}
        {actions.length > 0 && (
          <div className="relative">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowActionPicker(!showActionPicker)}
              disabled={!!loading}
            >
              <ArrowRightLeft className="mr-1.5 h-3.5 w-3.5" />
              {t('bulk.changeStatus', 'Change Status')}
            </Button>

            {/* Action dropdown */}
            {showActionPicker && (
              <div className="absolute bottom-full left-0 mb-1 rounded-md border bg-background shadow-lg py-1 min-w-[160px]">
                {actions.map((action) => (
                  <button
                    key={action.name}
                    className="w-full text-left px-3 py-1.5 text-sm hover:bg-muted transition-colors"
                    onClick={() => handleBatchAction(action.name, action.label ?? action.name)}
                  >
                    {action.label ?? action.name}
                    {action.toStatus && (
                      <span className="ml-2 text-xs text-muted-foreground">→ {action.toStatus}</span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Batch Delete */}
        <Button
          variant="outline"
          size="sm"
          className="text-destructive hover:text-destructive/80"
          onClick={() => setShowDeleteConfirm(true)}
          disabled={!!loading}
        >
          {loading === 'delete' ? (
            <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
          ) : (
            <Trash2 className="mr-1.5 h-3.5 w-3.5" />
          )}
          {t('bulk.delete', 'Delete')}
        </Button>

        {/* Export Selected */}
        <Button variant="outline" size="sm" onClick={handleExportSelected} disabled={!!loading}>
          <Download className="mr-1.5 h-3.5 w-3.5" />
          {t('bulk.export', 'Export')}
        </Button>

        <div className="h-4 w-px bg-border" />

        {/* Clear Selection */}
        <Button
          variant="ghost"
          size="sm"
          onClick={onClearSelection}
          className="text-muted-foreground"
        >
          <X className="mr-1 h-3.5 w-3.5" />
          {t('bulk.clear', 'Clear')}
        </Button>
      </div>
    </>
  );
}
